import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import Swal from 'sweetalert2';
import {
  getQuestionnaireTemplateConfig,
  QuestionnaireTemplateConfig,
} from 'src/app/constants/questionnaire-template-registry';
import { QuestionnaireHistoryItem } from 'src/app/interfaces/Questionnaire/IQuestionnaireHistory';
import { getQuestionnaireActorDisplayValue } from 'src/app/utils/questionnaire-history-actor';
import {
  formatQuestionnaireSubScores,
  getQuestionnaireScoreBadge,
  QuestionnaireScoreBadge,
  getQuestionnaireScoreState,
  QuestionnaireScoreState,
} from 'src/app/utils/questionnaire-history-score';
import { date_format, datetime_format } from 'src/date/date.configuration';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { SwalToastService } from 'src/app/services/swal.service';

interface QuestionnaireHistoryTableRow {
  id?: number;
  questionnaireName: string;
  score?: string;
  visitContext: string;
  createdDate: string;
  submittedDate: string;
  submittedBy: string;
  isSubmitted?: boolean;
  __historyItem: QuestionnaireHistoryItem;
  [key: string]: any;
}

@Component({
  selector: 'app-questionnaire-history-table',
  templateUrl: './questionnaire-history-table.component.html',
  styleUrls: ['./questionnaire-history-table.component.css'],
  standalone: false,
})
export class QuestionnaireHistoryTableComponent implements OnChanges {
  private static readonly SCORE_GRAPH_TEMPLATE_IDS = new Set<number>([
    3, // NIH Stroke
    5, // Geriatric Depression Scale
    6, // Beck Depression Inventory
    8, // Berg Balance Scale
    10, // SLDC
    12, // DN4
    13, // EQ-5D
    15, // PAINAD
    17, // MFIS
    18, // MRC
    19, // Mini-BESTest
    20, // Fugl-Meyer
    21, // Trunk Control Test
    22, // Trunk Impairment Scale
    23, // Six Minute Walk Test
    24, // Ten Meter Walk Test
    25, // Functional Ambulation Category
    26, // Functional Gait Assessment
    27, // Timed Up and Go
    29, // Barthel Index
    30, // FIM
  ]);

  @Input() questionnaireTemplateId?: number;
  @Input() showAddButton = false;
  @Input() currentVisitId?: string | number | null;
  @Input() historyItems: QuestionnaireHistoryItem[] = [];
  @Input() isLoading = false;
  @Input() error = '';
  @Output() addRequested = new EventEmitter<number>();
  @Output() submissionSelected = new EventEmitter<QuestionnaireHistoryItem>();
  @Output() questionnaireDeleted = new EventEmitter<QuestionnaireHistoryItem>();

  isChartVisible = false;
  tableRows: QuestionnaireHistoryTableRow[] = [];
  addButtonDisabled = false;
  addButtonDisabledReason = '';

  customActions = [
    {
      iconType: 'icons:eye',
      tooltip: 'View',
      handler: (row: QuestionnaireHistoryTableRow) =>
        this.submissionSelected.emit({
          ...row.__historyItem,
          canEdit: false,
        }),
      isVisible: (row: QuestionnaireHistoryTableRow) =>
        row.__historyItem.canView !== false,
    },
    {
      iconType: 'icons:form',
      tooltip: 'Edit',
      handler: (row: QuestionnaireHistoryTableRow) =>
        this.submissionSelected.emit(row.__historyItem),
      isVisible: (row: QuestionnaireHistoryTableRow) =>
        row.__historyItem.canEdit === true,
    },
    {
      iconType: 'icons:delete',
      tooltip: 'Delete',
      handler: (row: QuestionnaireHistoryTableRow) =>
        this.onDeleteQuestionnaire(row),
      isVisible: (row: QuestionnaireHistoryTableRow) =>
        row.__historyItem.canEdit === true &&
        row.__historyItem.isSubmitted === false,
    },
  ];

  constructor(
    private readonly translate: TranslateService,
    private readonly questionnaireService: DynamicQuestionnaireService,
    private readonly swalToastService: SwalToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['historyItems'] ||
      changes['questionnaireTemplateId'] ||
      changes['currentVisitId'] ||
      changes['showAddButton']
    ) {
      this.isChartVisible = false;
      this.tableRows = this.buildTableRows(this.historyItems ?? []);
      this.syncAddButtonState();
    }
  }

  onAddQuestionnaire(): void {
    if (this.addButtonDisabled) {
      return;
    }

    if (this.questionnaireTemplateId) {
      this.addRequested.emit(this.questionnaireTemplateId);
    }
  }

  toggleChartView(): void {
    this.isChartVisible = !this.isChartVisible;
  }

  onDeleteQuestionnaire(row: QuestionnaireHistoryTableRow): void {
    const questionnaireId = Number(
      row.__historyItem.questionnaireId ?? row.__historyItem.id,
    );

    if (!questionnaireId) {
      return;
    }

    Swal.fire({
      title: this.translate.instant('Delete questionnaire draft'),
      text: this.translate.instant(
        'This will permanently remove this draft. This action cannot be undone.',
      ),
      showCancelButton: true,
      cancelButtonText: this.translate.instant('Cancel'),
      confirmButtonText: this.translate.instant('Ok'),
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.questionnaireService
        .deleteQuestionnaireDraft(questionnaireId)
        .subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Questionnaire draft deleted successfully',
            );
            this.tableRows = this.tableRows.filter((r) => r !== row);
            this.historyItems = this.historyItems.filter(
              (item) => item !== row.__historyItem,
            );
            this.syncAddButtonState();
            this.questionnaireDeleted.emit(row.__historyItem);
          },
          error: () => {
            this.swalToastService.toastError(
              'Unable to delete questionnaire draft',
            );
          },
        });
    });
  }

  getColumns(): string[] {
    const columns = ['Questionnaire'];

    if (this.shouldShowScoreColumn()) {
      columns.push('Score');
    }

    columns.push(
      'Visit / Episode',
      'Created date',
      'Submitted',
      'Submitted by',
    );

    return columns;
  }

  getRowFields(): string[] {
    const rowFields = ['questionnaireName'];

    if (this.shouldShowScoreColumn()) {
      rowFields.push('score');
    }

    rowFields.push(
      'visitContext',
      'createdDate',
      'submittedDate',
      'submittedBy',
    );

    return rowFields;
  }

  getQuestionnaireTitle(): string {
    return (
      this.getTemplateConfig()?.title ??
      this.translate.instant('Questionnaire history')
    );
  }

  getAddPermissions(): string[] {
    return this.getTemplateConfig()?.addPermissions ?? [];
  }

  shouldShowScoreColumn(): boolean {
    return this.historyItems.some(
      (item: QuestionnaireHistoryItem) =>
        this.getScoreState(item).kind !== 'none',
    );
  }

  canShowGraph(): boolean {
    return this.isScoreGraphTemplate() && this.getScoredPointCount() >= 2;
  }

  private buildTableRows(
    items: QuestionnaireHistoryItem[],
  ): QuestionnaireHistoryTableRow[] {
    return items.map((item: QuestionnaireHistoryItem, index: number) => {
      const scoreState = this.getScoreState(item);
      const subScoresText =
        scoreState.kind === 'subScores'
          ? formatQuestionnaireSubScores(scoreState.subScores, (label) =>
              this.translate.instant(label),
            )
          : '';
      const scoreField =
        scoreState.kind === 'scored'
          ? String(scoreState.value)
          : scoreState.kind === 'subScores'
            ? subScoresText
            : scoreState.kind === 'incomplete'
              ? scoreState.label
              : 'N/A';

      const scoreBadge =
        scoreState.kind === 'scored'
          ? {
              text: String(scoreState.value),
              className: `score-pill score-pill--${scoreState.tone}`,
              title: scoreState.label,
            }
          : scoreState.kind === 'subScores'
            ? {
                text: subScoresText,
                className: 'score-pill score-pill--neutral',
                title: subScoresText,
              }
            : scoreState.kind === 'incomplete'
              ? {
                  text: scoreState.label,
                  className: 'score-pill score-pill--incomplete',
                  title: scoreState.label,
                }
              : null;

      return {
        id: Number(item.questionnaireId ?? item.id ?? index) || index + 1,
        questionnaireName: this.getQuestionnaireName(item),
        score: scoreField,
        visitContext: this.getVisitContext(item),
        createdDate: this.getCreatedDate(item),
        submittedDate: this.getSubmittedDate(item),
        submittedBy: this.getActor(item),
        isSubmitted: item.isSubmitted,
        __historyItem: item,
        ...(scoreBadge
          ? {
              __badge__score: {
                text: scoreBadge.text,
                className: scoreBadge.className,
                title: scoreBadge.title,
              },
            }
          : {}),
      };
    });
  }

  private getQuestionnaireName(item: QuestionnaireHistoryItem): string {
    return (
      item.title ||
      item.questionnaire?.title ||
      this.translate.instant(this.getQuestionnaireTitle())
    );
  }

  private getVisitContext(item: QuestionnaireHistoryItem): string {
    const visit = item.visitLabel ?? item.visitId?.toString();
    const episode = item.episodeCareLabel ?? item.episodeCareId?.toString();

    if (visit && episode) {
      return `Visit ${visit} / EOC ${episode}`;
    }

    if (visit) {
      return `Visit ${visit}`;
    }

    if (episode) {
      return `EOC ${episode}`;
    }

    return 'N/A';
  }

  private getCreatedDate(item: QuestionnaireHistoryItem): string {
    return this.formatDate(item.createdDate ?? item.creationDate, false);
  }

  private getSubmittedDate(item: QuestionnaireHistoryItem): string {
    return this.formatDate(
      item.submittedDate ??
        item.lastUpdateDate ??
        item.createdDate ??
        item.creationDate,
      true,
    );
  }

  private getActor(item: QuestionnaireHistoryItem): string {
    return getQuestionnaireActorDisplayValue(item);
  }

  private getScoreBadge(
    item: QuestionnaireHistoryItem,
  ): QuestionnaireScoreBadge | null {
    return getQuestionnaireScoreBadge(item, this.questionnaireTemplateId);
  }

  private getScoreState(
    item: QuestionnaireHistoryItem,
  ): QuestionnaireScoreState {
    return getQuestionnaireScoreState(item);
  }

  private getTemplateConfig(): QuestionnaireTemplateConfig | undefined {
    return getQuestionnaireTemplateConfig(this.questionnaireTemplateId);
  }

  private isScoreGraphTemplate(): boolean {
    const templateId = Number(this.questionnaireTemplateId ?? 0);
    return QuestionnaireHistoryTableComponent.SCORE_GRAPH_TEMPLATE_IDS.has(
      templateId,
    );
  }

  private getScoredPointCount(): number {
    return this.historyItems.filter(
      (item: QuestionnaireHistoryItem) => !!this.getScoreBadge(item),
    ).length;
  }

  private formatDate(value?: string, includeTime: boolean = false): string {
    if (!value) {
      return 'N/A';
    }

    const parsed = moment(
      value,
      [moment.ISO_8601, datetime_format, date_format],
      true,
    );
    return parsed.isValid()
      ? parsed.format(includeTime ? datetime_format : date_format)
      : value;
  }

  private syncAddButtonState(): void {
    const hasCurrentVisitDraft = this.hasCurrentVisitDraft();
    this.addButtonDisabled =
      this.isLoading || (this.showAddButton && hasCurrentVisitDraft);
    this.addButtonDisabledReason = this.addButtonDisabled
      ? 'A draft questionnaire already exists for this visit. Open it to edit.'
      : '';
  }

  private hasCurrentVisitDraft(): boolean {
    const currentVisitId = this.toFiniteNumber(this.currentVisitId);
    const templateId = this.toFiniteNumber(this.questionnaireTemplateId);

    if (currentVisitId === null || templateId === null) {
      return false;
    }

    return this.historyItems.some((item: QuestionnaireHistoryItem) => {
      const itemTemplateId = this.toFiniteNumber(item.questionnaireTemplateId);
      const itemVisitId = this.toFiniteNumber(item.visitId);

      return (
        item.isSubmitted === false &&
        itemTemplateId === templateId &&
        itemVisitId === currentVisitId
      );
    });
  }

  private toFiniteNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }
}
